<script setup lang="ts">
import { useRouter } from 'vue-router';
import BuildStatusBadge from './BuildStatusBadge.vue';
import StageBar from './StageBar.vue';
import type { Build } from '@kilnci/shared';
import { formatRelativeTime, formatDurationShort } from '../utils/date';

defineProps<{
  builds: Build[];
}>();

const router = useRouter();

type TriggerChip = { kind: 'manual' | 'scheduled' | 'author'; label: string };

function getAuthorLabel(labels?: Record<string, string>): string | null {
  if (!labels) return null;
  return labels['git.author.name'] ||
    labels['git.author.email']?.split('@')[0] ||
    labels['mr.author.username'] ||
    null;
}

function getMrLabel(labels?: Record<string, string>): string | null {
  if (!labels || !labels['mr.id']) return null;
  return `MR !${labels['mr.id']}`;
}

function isManualBuild(build: Build): boolean {
  return build.triggerSource === 'ui' || build.triggerSource === 'api' || build.triggerSource === 'cli';
}

function isScheduledBuild(build: Build): boolean {
  return build.triggerSource === 'schedule';
}

function getTriggerUserLabel(labels?: Record<string, string>): string | null {
  if (!labels) return null;
  return labels['trigger.user.name'] ||
    labels['trigger.user.email']?.split('@')[0] ||
    null;
}

function getTriggerChip(build: Build): TriggerChip | null {
  if (isManualBuild(build)) {
    return { kind: 'manual', label: getTriggerUserLabel(build.labels.prefers) || 'Manual' };
  }
  if (isScheduledBuild(build)) {
    return { kind: 'scheduled', label: 'Scheduled' };
  }
  const author = getAuthorLabel(build.labels.prefers);
  if (author) return { kind: 'author', label: author };
  return null;
}

function getDuration(build: Build): string | null {
  const dur = formatDurationShort(build.startedAt, build.finishedAt);
  if (!dur || dur === '0s') return null;
  return dur;
}
</script>

<template>
  <div class="build-list">
    <div v-if="builds.length === 0" class="empty">
      No builds found.
    </div>
    <TransitionGroup v-else name="build-list" tag="div" class="build-list-container">
      <div
        class="build-item"
        :class="`status-${build.status}`"
        v-for="build in builds"
        :key="build.id"
      >
        <div class="build-link" @click="router.push(`/builds/${build.id}`)">
          <div class="build-header">
            <BuildStatusBadge :status="build.status" />
            <span class="build-header-meta">
              <code class="build-id" :title="build.id">{{ build.id.substring(0, 8) }}</code>
              <span class="build-time">
                {{ formatRelativeTime(build.createdAt) }}
                <span v-if="getDuration(build)" class="build-duration"> · {{ getDuration(build) }}</span>
              </span>
            </span>
          </div>
          <div class="build-info">
            <span
              class="build-repo"
              :title="build.repo"
              @click.stop="router.push({ name: 'repository-detail', params: { slug: build.organizationSlug, repoName: build.repositoryName } })"
            >
              <span class="build-org">{{ build.organizationSlug }}/</span>{{ build.repositoryName }}
            </span>
            <div class="build-branch-group">
              <span v-if="build.pipelineName" class="build-pipeline-adj" :title="`Pipeline: ${build.pipelineName}`">
                {{ build.pipelineName }} ·
              </span>
              <span
                class="build-branch"
                @click.stop="router.push({ name: 'repository-detail', params: { slug: build.organizationSlug, repoName: build.repositoryName }, query: { branch: build.branch } })"
              >
                {{ build.branch }}
              </span>
            </div>
          </div>
          <div v-if="getTriggerChip(build) || getMrLabel(build.labels.prefers)" class="build-labels">
            <span
              v-if="getTriggerChip(build)"
              class="build-label"
              :class="`${getTriggerChip(build)!.kind}-label`"
            >
              <svg v-if="getTriggerChip(build)!.kind === 'scheduled'" class="label-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <svg v-else class="label-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="12" cy="8" r="4"></circle>
                <path d="M4 20c0-4 4-6 8-6s8 2 8 6"></path>
              </svg>
              {{ getTriggerChip(build)!.label }}
            </span>
            <span v-if="getMrLabel(build.labels.prefers)" class="build-label mr-label">{{ getMrLabel(build.labels.prefers) }}</span>
          </div>
          <StageBar v-if="build.stages && build.stages.length > 0" :stages="build.stages" :build-status="build.status" />
          <div v-else class="stage-placeholder" aria-hidden="true"></div>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.build-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.build-list-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.empty {
  text-align: center;
  padding: 40px;
  color: var(--color-text-secondary);
}

.build-item {
  background: var(--color-card-bg);
  border-radius: 12px;
  box-shadow: 0 1px 3px var(--color-card-shadow);
  border: 1px solid var(--color-card-border);
  overflow: hidden;
  transition: box-shadow 0.2s, transform 0.2s;
}

.build-item:hover {
  box-shadow: 0 4px 6px var(--color-card-shadow);
}

.build-item.status-running {
  border-color: var(--color-running-border);
  animation: running-glow 2s ease-in-out infinite;
}
.build-item.status-pending { border-color: var(--color-warning-border); }

@keyframes running-glow {
  0%, 100% {
    box-shadow: 0 0 2px var(--color-running-border);
  }
  50% {
    box-shadow: 0 0 8px var(--color-running-border);
  }
}

/* TransitionGroup animations */
.build-list-enter-active {
  transition: all 0.4s ease-out;
}

.build-list-leave-active {
  transition: all 0.3s ease-in;
  position: absolute;
  width: 100%;
}

.build-list-enter-from {
  opacity: 0;
  transform: translateX(-20px) scale(0.95);
}

.build-list-leave-to {
  opacity: 0;
  transform: translateX(20px) scale(0.95);
}

.build-list-move {
  transition: transform 0.4s ease;
}

.build-link {
  display: block;
  padding: 16px;
  cursor: pointer;
  color: inherit;
}

.build-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.build-header-meta {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.build-id {
  font-family: monospace;
  font-size: 0.8rem;
  color: var(--color-text-muted);
  background: transparent;
  padding: 0;
}

.build-time {
  font-size: 0.85rem;
  color: var(--color-text-secondary);
}

.build-duration {
  color: var(--color-text-muted);
}

.build-info {
  display: flex;
  gap: 16px;
  align-items: center;
}

.build-repo {
  font-weight: 500;
  color: var(--color-text-primary);
  cursor: pointer;
}

.build-org {
  color: var(--color-text-muted);
  font-weight: 400;
}

.build-repo:hover {
  color: var(--color-accent);
}

.build-branch-group {
  margin-left: auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.build-branch {
  font-family: monospace;
  font-size: 0.9rem;
  padding: 2px 8px;
  background: var(--color-badge-default-bg);
  color: var(--color-badge-default-text);
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
}

.build-branch:hover {
  background: var(--color-accent);
  color: var(--color-btn-primary-text);
}

.build-pipeline-adj {
  font-family: monospace;
  font-size: 0.85rem;
  color: var(--color-text-muted);
}

.build-labels {
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.build-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  padding: 2px 8px;
  border-radius: 4px;
}

.label-icon {
  flex-shrink: 0;
}

.author-label,
.manual-label {
  background: var(--color-info-bg);
  color: var(--color-info-text);
}

.mr-label {
  background: var(--color-warning-bg);
  color: var(--color-warning-text);
}

.scheduled-label {
  background: var(--color-pending-bg);
  color: var(--color-pending-text);
}

.stage-placeholder {
  height: 8px;
  margin-top: 16px;
  border-radius: 4px;
  background: var(--color-card-border);
  opacity: 0.4;
}

/* Tablet breakpoint (768px) */
@media (max-width: 768px) {
  .build-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .build-repo {
    order: 1;
    width: 100%;
    word-break: break-word;
  }

  .build-branch-group {
    order: 2;
    margin-left: 0;
    align-items: flex-start;
    max-width: 100%;
  }

  .build-branch {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

/* Phone breakpoint (480px) */
@media (max-width: 480px) {
  .build-link {
    padding: var(--mobile-padding, 12px);
  }

  .build-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .build-time {
    font-size: 0.8rem;
  }

  .build-repo {
    font-size: 0.95rem;
    word-break: break-all;
  }

  .build-labels {
    margin-top: 12px;
  }
}
</style>
